import { useMemo } from "react";
import L from "leaflet";
import { Marker, useMap } from "react-leaflet";
import ReactDOMServer from "react-dom/server";
import classNames from "classnames";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import { Actor } from "../../models/Actor";
import {
  getActorAvatarCssColor,
  getActorAvatarInitial,
  getActorAvatarSoftStyle,
} from "../../helpers/actorAvatar";
import { CAMERA_MARKER_Z_INDEX_OFFSET } from "./InteractionPointPreview";

const PLAYER_ID = "player";

export type AttachmentMember =
  | { kind: "actor"; actor?: Actor; actorId: string }
  | { kind: "prop"; imageUrl?: string };

/** A host marker or a guest chip, looking like the map's own markers. */
function Bubble(props: {
  member: AttachmentMember;
  takeColor?: string;
  className: string;
}) {
  const { member, takeColor, className } = props;
  // Like the map markers: a border in the take location's colour.
  const border = takeColor ? { borderColor: takeColor } : undefined;

  if (member.kind === "prop")
    return (
      <span className={classNames(className, "sequence-attachment__prop")} style={border}>
        {member.imageUrl ? (
          <img src={member.imageUrl} alt="" />
        ) : (
          <ViewInArOutlinedIcon fontSize="inherit" />
        )}
      </span>
    );
  if (member.actorId === PLAYER_ID)
    return (
      <span
        className={classNames(className, "sequence-attachment__player")}
        style={takeColor ? { ...border, background: takeColor } : undefined}
      >
        P
      </span>
    );
  return (
    <span
      className={className}
      style={{ ...getActorAvatarSoftStyle(getActorAvatarCssColor(member.actor)), ...border }}
    >
      {getActorAvatarInitial(member.actor?.workingName)}
    </span>
  );
}

/**
 * A host marker with what is attached to it docked on its rim, in the sequence
 * state: a prop in an actor's hand at the lower right (Prop to Player), or an
 * actor on a prop at the upper left (Player to Prop). Same footprint and zoom
 * scale as the map markers; clicks go through to the marker below (pickers).
 */
export default function SequenceAttachmentMarker(props: {
  x: number;
  y: number;
  host: AttachmentMember;
  guests: AttachmentMember[];
  /** Prop to Player: the guest is a prop in the host's hand (lower right). */
  held: boolean;
  /** Got off the prop after a Detach: standing by it, no longer attached. */
  beside?: boolean;
  /** The node in focus targets the host's position: rim it. */
  isTarget?: boolean;
  dot?: { uncertain: boolean };
  title: string;
  /** The take location's colour, as the markers use it (CSS colour). */
  takeColor?: string;
}) {
  const map = useMap();
  const { x, y, host, guests, held, beside, isTarget, dot, title, takeColor } = props;
  const latLng = useMemo(() => map.unproject([512 * x, 512 * y], 1), [map, x, y]);

  const icon = useMemo(() => {
    const more = guests.length - 1;
    // A zero-size icon on the point, like the markers: the marker centres and
    // scales itself on it (Viewport3D.css, --map-marker-scale).
    return new L.DivIcon({
      className: "sequence-attachment-icon",
      iconSize: [0, 0],
      iconAnchor: [0, 0],
      html: ReactDOMServer.renderToString(
        <div
          className={classNames("sequence-attachment", {
            "sequence-attachment--held": held,
            "sequence-attachment--riding": !held,
            "sequence-attachment--beside": beside,
            "sequence-attachment--target": isTarget,
          })}
          title={title}
        >
          <Bubble member={host} takeColor={takeColor} className="sequence-attachment__host" />
          {guests[0] && (
            <Bubble member={guests[0]} takeColor={takeColor} className="sequence-attachment__guest" />
          )}
          {more > 0 && <span className="sequence-attachment__more">+{more}</span>}
          {dot && (
            <span
              className={classNames("sequence-attachment__dot", {
                "sequence-attachment__dot--maybe": dot.uncertain,
              })}
            />
          )}
        </div>,
      ),
    });
  }, [beside, dot, guests, held, host, isTarget, takeColor, title]);

  return (
    <Marker
      position={latLng}
      icon={icon}
      interactive={false}
      keyboard={false}
      zIndexOffset={CAMERA_MARKER_Z_INDEX_OFFSET - 1}
    />
  );
}
